/**
 * Link Still-missing blockers to flexible Do-next actions.
 * Prefer explicit nextActions (any kind) over raw search pools.
 */

function normalizeKey(text) {
  return String(text || '').trim().toLowerCase();
}

function actionKeyForSearch(query) {
  const q = String(query || '').trim();
  return q ? `search:${q}` : null;
}

export function actionKeyForNextAction(action, index = 0) {
  if (!action) return null;
  const kind = String(action.kind || 'decide');
  if (kind === 'search' && action.query) return actionKeyForSearch(action.query);
  if (kind === 'ask') return 'ask';
  if (kind === 'place') return 'place';
  if (kind === 'organize') return 'organize';
  const label = String(action.label || '').trim().toLowerCase();
  return label ? `next:${kind}:${label}` : `next:${kind}:${index}`;
}

function scoreTextOverlap(left, right) {
  const a = normalizeKey(left);
  const b = normalizeKey(right);
  if (!a || !b) return 0;
  if (a === b) return 12;
  if (a.includes(b) || b.includes(a)) return 8;
  const tokens = a.split(/[^a-z0-9]+/).filter(t => t.length > 3);
  let score = 0;
  for (const token of tokens) {
    if (b.includes(token)) score += 2;
  }
  return score;
}

function scoreBlockerToSearch(blocker, item) {
  if (!item?.query) return 0;
  let score = 0;
  if (normalizeKey(item.blockerTitle) && normalizeKey(item.blockerTitle) === normalizeKey(blocker?.title)) {
    score += 20;
  }
  score += scoreTextOverlap(blocker?.title, item.query);
  score += scoreTextOverlap(blocker?.title, item.reason);
  score += scoreTextOverlap(blocker?.why, item.query);
  score += scoreTextOverlap(blocker?.why, item.reason);
  score += scoreTextOverlap(blocker?.howToFill, item.query);
  score += scoreTextOverlap(blocker?.howToFill, item.reason);
  return score;
}

function scoreBlockerToNextAction(blocker, action) {
  if (!action) return 0;
  let score = 0;
  if (normalizeKey(action.blockerTitle) && normalizeKey(action.blockerTitle) === normalizeKey(blocker?.title)) {
    score += 20;
  }
  score += scoreTextOverlap(blocker?.title, action.label);
  score += scoreTextOverlap(blocker?.title, action.query);
  score += scoreTextOverlap(blocker?.title, action.why);
  score += scoreTextOverlap(blocker?.title, action.question);
  score += scoreTextOverlap(blocker?.howToFill, action.query);
  score += scoreTextOverlap(blocker?.howToFill, action.label);
  score += scoreTextOverlap(blocker?.howToFill, action.kind);
  return score;
}

function buildSearchPool(searchSuggestions, nextActions) {
  const pool = [];
  const seen = new Set();
  const add = item => {
    const query = String(item?.query || '').trim();
    if (!query) return;
    const key = query.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    pool.push({
      query,
      reason: String(item?.reason || item?.why || item?.label || '').trim(),
      imageUrl: item?.imageUrl || '',
      blockerTitle: String(item?.blockerTitle || '').trim(),
    });
  };

  for (const item of searchSuggestions || []) add(item);
  for (const action of nextActions || []) {
    if (action?.kind === 'search' && action?.query) {
      add({
        query: action.query,
        reason: action.why || action.label,
        blockerTitle: action.blockerTitle,
        imageUrl: action.imageUrl,
      });
    }
  }
  return pool;
}

function claimNextAction(action, index, usedActionKeys) {
  const actionKey = actionKeyForNextAction(action, index);
  if (!actionKey || usedActionKeys.has(actionKey)) return null;
  usedActionKeys.add(actionKey);
  return {
    actionKey,
    actionKind: action.kind || 'decide',
    nextAction: action,
    searchQuery: action.kind === 'search' ? action.query : undefined,
    imageUrl: action.imageUrl,
    reason: action.why || action.label,
  };
}

/**
 * @returns {Array<{
 *   blockerIndex: number,
 *   mark: number,
 *   blocker: object,
 *   actionKey: string,
 *   actionKind: string,
 *   nextAction?: object,
 *   searchQuery?: string,
 *   imageUrl?: string,
 *   reason?: string,
 * }>}
 */
export function linkBlockersToActions({
  blockers = [],
  searchSuggestions = [],
  nextActions = [],
} = {}) {
  const list = (Array.isArray(blockers) ? blockers : []).slice(0, 3);
  if (!list.length) return [];

  const searchPool = buildSearchPool(searchSuggestions, nextActions);
  const usedActionKeys = new Set();
  const links = [];

  const claimSearch = item => {
    const actionKey = actionKeyForSearch(item?.query);
    if (!actionKey || usedActionKeys.has(actionKey)) return null;
    usedActionKeys.add(actionKey);
    return {
      actionKey,
      actionKind: 'search',
      searchQuery: item.query,
      imageUrl: item.imageUrl,
      reason: item.reason,
    };
  };

  // Pass 1: all explicit blockerTitle matches on any nextAction kind.
  list.forEach((blocker, index) => {
    const titleKey = normalizeKey(blocker?.title);
    if (!titleKey) return;

    (nextActions || []).forEach((action, actionIndex) => {
      if (normalizeKey(action?.blockerTitle) !== titleKey) return;
      const claimed = claimNextAction(action, actionIndex, usedActionKeys);
      if (claimed) {
        links.push({ blockerIndex: index, mark: index + 1, blocker, ...claimed });
      }
    });
  });

  // Pass 2: best nextAction content match for remaining blockers.
  list.forEach((blocker, index) => {
    if (links.some(link => link.blockerIndex === index)) return;

    let bestIndex = -1;
    let bestScore = 0;
    (nextActions || []).forEach((action, actionIndex) => {
      const key = actionKeyForNextAction(action, actionIndex);
      if (!key || usedActionKeys.has(key)) return;
      const score = scoreBlockerToNextAction(blocker, action);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = actionIndex;
      }
    });

    if (bestIndex >= 0 && bestScore > 0) {
      const claimed = claimNextAction(nextActions[bestIndex], bestIndex, usedActionKeys);
      if (claimed) {
        links.push({ blockerIndex: index, mark: index + 1, blocker, ...claimed });
      }
    }
  });

  // Pass 3: search pool for anything still unlinked.
  list.forEach((blocker, index) => {
    if (links.some(link => link.blockerIndex === index)) return;

    const titleKey = normalizeKey(blocker?.title);
    const titledSearch = searchPool.find(
      item => normalizeKey(item.blockerTitle) === titleKey
        && !usedActionKeys.has(actionKeyForSearch(item.query))
    );
    if (titledSearch) {
      const claimed = claimSearch(titledSearch);
      if (claimed) {
        links.push({ blockerIndex: index, mark: index + 1, blocker, ...claimed });
        return;
      }
    }

    let best = null;
    let bestScore = 0;
    for (const item of searchPool) {
      const key = actionKeyForSearch(item.query);
      if (!key || usedActionKeys.has(key)) continue;
      const score = scoreBlockerToSearch(blocker, item);
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    }
    if (best && bestScore > 0) {
      const claimed = claimSearch(best);
      if (claimed) {
        links.push({ blockerIndex: index, mark: index + 1, blocker, ...claimed });
        return;
      }
    }

    const nextUnused = searchPool.find(item => !usedActionKeys.has(actionKeyForSearch(item.query)));
    if (nextUnused) {
      const claimed = claimSearch(nextUnused);
      if (claimed) {
        links.push({ blockerIndex: index, mark: index + 1, blocker, ...claimed });
      }
    }
  });

  return links.sort((a, b) => a.blockerIndex - b.blockerIndex);
}

export function getActionKeyMarks(links) {
  const map = new Map();
  for (const link of links) {
    if (!map.has(link.actionKey)) map.set(link.actionKey, []);
    map.get(link.actionKey).push(link.mark);
  }
  return map;
}

/** Group aligned action links by missing-point (blocker) index. */
export function groupLinksByBlocker(links = []) {
  const map = new Map();
  for (const link of links) {
    const index = link?.blockerIndex;
    if (!Number.isInteger(index)) continue;
    if (!map.has(index)) map.set(index, []);
    map.get(index).push(link);
  }
  return map;
}

/** Deduplicate missing points by normalized title, keeping first occurrence. */
export function dedupeBlockers(blockers = []) {
  const list = Array.isArray(blockers) ? blockers : [];
  const seen = new Set();
  const unique = [];
  for (const blocker of list) {
    const title = String(blocker?.title || '').trim();
    if (!title) continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(blocker);
  }
  return unique.slice(0, 3);
}
